import { useCallback, useEffect } from 'react';

// the track asked about plays from a little before it starts to a little after it ends, over and over
export const REVIEW_LEAD_SEC = 0.3;
export const REVIEW_TAIL_SEC = 0.5;

const loopStart = focus => Math.max(0, focus.t0 - REVIEW_LEAD_SEC);

/**
 * Loops the track being checked (ReviewMode) on the player: back to just before it starts once the video is past
 * just after it ends, or once the video itself ends, which comes first for a track seen until the last frame (a
 * herd in view the whole flight) — the video then stops, so it is played again too.
 *
 * @param open the check is open
 * @param focus the track asked about, { t0, t1 } in seconds, or null
 * @param fps frames per second of the frame numbers
 * @param frame the frame shown
 * @param playerRef the OverlayPlayer's handle (seekTo, video)
 * @returns start(focus): jump to a track and play it
 */
const useReviewLoop = ({ open, focus, fps, frame, playerRef }) => {
	const start = useCallback(
		track => {
			const player = playerRef.current;
			player?.seekTo(loopStart(track));
			player
				?.video?.()
				?.play?.()
				?.catch?.(() => {});
		},
		[playerRef]
	);

	useEffect(() => {
		if (!open || !focus || !fps) return;
		if (frame / fps > focus.t1 + REVIEW_TAIL_SEC) playerRef.current?.seekTo(loopStart(focus));
	}, [open, focus, fps, frame, playerRef]);

	useEffect(() => {
		if (!open || !focus) return undefined;
		const video = playerRef.current?.video?.();
		if (!video) return undefined;
		const onEnded = () => start(focus);
		video.addEventListener('ended', onEnded);
		return () => video.removeEventListener('ended', onEnded);
	}, [open, focus, playerRef, start]);

	return start;
};

export default useReviewLoop;
